import { splitX } from '../../core/utils';

/** The display position of the paginator relative to its table. */
export type PaginatorPosition = 'top' | 'bottom' | 'both';

/**
 * A compact external representation of the table configuration.\
 */
export interface TableConfig {
    frozenCol: string;
    columnConfigs: TableColumnConfig[];
    paginatorPosition: PaginatorPosition;
    defaultTablePageSize: number;
    tablePageSize: number;
    columnsReorderable: boolean;

    /** @deprecated legacy compatibility */
    columResults: any[];
    /** @deprecated legacy compatibility */
    isReorderable: boolean;
}

/** Raw table config values, as used internally in the table service and components. */
export interface DevTableConfigValues {
    /** How many columns are frozen */
    frozenCol: string;
    /** The list of hidden columns (by id) */
    hiddenCols: string[];
    /** The order of the columns (by id, may be _null_) */
    colOrder: string[] | null;
    /** The position of the paginator relative to the table */
    paginatorPosition: PaginatorPosition;
    /** The global default table page size (may be _null_)\
     *  For a reliable actual page size value, use the `effectiveTablePageSize` function. */
    defaultTablePageSize: number | null;
    /** The local table page size (may be _null_)\
     *  For a reliable actual page size value, use the `effectiveTablePageSize` function. */
    tablePageSize: number | null;
    /** Whether columns can be reordered by the user */
    columnsReorderable: boolean;
    /** Whether the table filter is shown */
    showTableFilter: boolean;
}

export type DevTableConfigChanges = {
    [K in keyof DevTableConfigValues]?: {
        previous: DevTableConfigValues[K];
        current: DevTableConfigValues[K];
    };
}

/**
 * The internal representation of the table configuration used within table components, which may contain additional properties or different types compared to the external `TableConfig` interface.
 * This allows for separation of concerns between the internal state management and the external API of the component.\
 * The combined `columnConfigs` is seperated into its defining aspects `hiddenCols` and `colOrder` to allow for easier management of these aspects both internally and when persisting user configurations.
 */
export interface DevTableConfig extends Readonly<DevTableConfigValues> {
    readonly changes: DevTableConfigChanges | null;
}

/** Creates an instance of the DevTableConfig with default values. */
export function defaultDevTableConfig(): DevTableConfig {
    return {
        hiddenCols: [],
        frozenCol: 'noneCol',
        colOrder: null,
        paginatorPosition: 'bottom',
        defaultTablePageSize: null,
        tablePageSize: null,
        columnsReorderable: false,
        showTableFilter: false,
        changes: null
    };
}

/** The configuration for a single column in the table, including its visibility and whether it is fixed. */
export interface TableColumnConfig {
    [key: string]: any; // allow for additional properties
    readonly id: string;
    readonly label: string;
    visible: boolean;
}

export interface TableColumnMeta extends TableColumnConfig {
    filterPath?: string; // optional path to the property used for filtering (if different from id)
    sortPath?: string; // optional path to the property used for sorting (if different from id)
}

/**
 * Deserializes a string of column IDs separated by commas into an array of column IDs.\
 * As the column IDs are represented by the criteria paths and may contain commas as part of the path definition, the string is only split
 * on top-level commas, ignoring commas that are inside brackets, quotes, or directly preceded by a backslash.
 * @param serializedIds The string of serialized column IDs.
 * @returns An array of column IDs, or `[]` if the input does not contain any splitable commas.
 */
export function deserializeColumnIds( serializedIds: string ): string[] {
    return splitX( serializedIds, ',', { brackets: ['()', '[]', '{}'], quotes: ['"', '\''], escapeChars: ['\\'] } );
}

/** Joins column IDs into a single string separated by commas. */
export function serializeColumnIds( columnIds: string[] ): string {
    return columnIds.join( ',' );
}

/** Resolves the effective table page size, considering the table-specific setting, the global default setting, and a local default value. */
export function effectiveTablePageSize( config: Partial<DevTableConfigValues>, localDefault: number = 10 ): number {
    return config.tablePageSize ?? config.defaultTablePageSize ?? localDefault;
}